import React, {Component} from 'react';
import {TopLoader} from '@sinclair-digital/sbg-storyline-components/src/components/TopLoader';
import {default as Search} from '@sinclair-digital/sbg-storyline-components/src/components/Search';
import {default as Button} from '@sinclair-digital/sbg-storyline-components/src/components/Button';
import BulkEditTable from './BulkEditTable';
import BulkEditControls from './BulkEditControls';
import TagPanel from './BulkEditTagPanel';
import {fetchJsonV1} from '@sinclair-digital/v2-content-service/lib/fetch';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import {not, splitEvery} from 'ramda';
import {
    assign,
    modify,
    set,
    elems,
    when,
} from 'partial.lenses';
import {
    FETCH_STATUS,
    PUBLISHED,
    UNPUBLISHED,
    PAGE_SIZE,
} from '../constants';
import classes from './StoryBulkEdit.scss';

const getIndexMap = results => results.reduce((accum, cur, idx) => ({...accum, [cur.uuid]: idx}), {});

const fetchStoryByUuid = uuid => fetchJsonV1('platform/facade/story/' + uuid, {'method': 'GET'});

const saveStory = story => fetchJsonV1('platform/facade/story/' + story.uuid,
    {
        method: 'PUT',
        body: JSON.stringify({data: {...story}}),
    });

const fetchSummariesByUuids = uuids => fetchJsonV1('/platform/package/summary',{
    method: 'POST',
    body: JSON.stringify(uuids),
});

const searchPackagesForText = needle => {
    return fetchJsonV1('/platform/search/package', {
        method: 'POST',
        body: JSON.stringify({
            'commonAndClause': {
                'type': ['STORY', 'ALERT', 'BIOGRAPHY', 'GALLERY'],
                'dateFrom': null,
                'dateThru': new Date().toISOString(),
                'textMatches': [needle],
                'indexSearchTerms': {},
            },
            'orClauses': [],
            'orderBy': 'descendingDate',
            'offset': 0,
        }),
    });
};

const searchTags = needle => fetchJsonV1(`/api/facade_tag_search?term=${needle}&limit=20`);

export default class StoryBulkEdit extends Component {
    state = {
        searchQuery: '',
        tagSearchQuery: '',
        results: [],
        tagList: [],
        allSelected: false,
        fetchStatus: '',
        showTagPanel: false,
    };

    setSearchQuery = (query) => this.setState({searchQuery: query})

    handleSearch = event => {
        event.preventDefault();
        this.setState({
            fetchStatus: FETCH_STATUS.PENDING,
            allSelected: false,
            pageIndex: 0,
            results: [],
        });

        searchPackagesForText(this.state.searchQuery)
            .then(
                uuids => fetchSummariesByUuids(uuids)
                    .then(results => {
                        if (results.length){
                            const pagedResults = splitEvery(PAGE_SIZE, results);

                            this.setState({
                                fetchStatus: FETCH_STATUS.SUCCESS,
                                pageIndex: 0,
                                results: pagedResults,
                            });
                        } else {
                            this.setState({
                                fetchStatus: FETCH_STATUS.EMPTY,
                            });
                        }
                    })
                    .catch(() => {
                        this.setState({
                            fetchStatus: FETCH_STATUS.FAIL,
                        });
                    })
            )
            .catch(() => {
                this.setState({
                    fetchStatus: FETCH_STATUS.FAIL,
                });
            });
    };

    handleTagSearch = query => {
        this.setState({
            fetchStatus: FETCH_STATUS.PENDING,
            tagList: [],
        });

        searchTags(query)
            .then(result => {
                // manually bump the tags weight to 1 so it will show up in the story view
                this.setState({tagList: result.map(tag => ({...tag, weight: 1}))});
            })
            .catch(() => {
                messageHandler('error', 'there was a problem with your tag search');
            })
            .finally(() => this.setState({fetchStatus: FETCH_STATUS.SUCCESS}));
    }

    handleItemSelect = i => this.setState(modify(['results', this.state.pageIndex, i, 'selected'], not));

    toggleSelectAll = () => {
        const selectedState = !this.state.allSelected;

        this.setState({
            allSelected: selectedState,
            results: set([this.state.pageIndex, elems, 'selected'], selectedState, this.state.results),
        });
    };

    pullAll = () => {
        const pageIndex = this.state.pageIndex;
        const indexMap = getIndexMap(this.state.results[pageIndex]);


        this.setState({
            fetchStatus: FETCH_STATUS.PENDING,
            results: set([this.state.pageIndex, elems, when(r => r.selected && r.status === PUBLISHED), 'fetchStatus'], FETCH_STATUS.PENDING, this.state.results),
        });

        Promise.all(
            this.state.results[pageIndex]
                // seems stupid to filter this twice...
                .filter(result => result.selected && result.status === PUBLISHED)
                .map(result => {
                    return fetchJsonV1('/platform/facade/story/unpublish/' + result.uuid,
                        {'method': 'PUT'})
                        .then(() =>
                            this.setState(assign(['results', pageIndex, indexMap[result.uuid]], {status: UNPUBLISHED, fetchStatus: FETCH_STATUS.SUCCESS}))
                        )
                        .catch(() => messageHandler('error', `unable to pull ${result.uuid}`));
                }),
        ).finally(() => this.setState({fetchStatus: FETCH_STATUS.SUCCESS}));
    };

    publishAll = () => {
        const pageIndex = this.state.pageIndex;
        const indexMap = getIndexMap(this.state.results[pageIndex]);

        this.setState({
            fetchStatus: FETCH_STATUS.PENDING,
            results: set([this.state.pageIndex, elems, when(r => r.selected && r.status === UNPUBLISHED), 'fetchStatus'], FETCH_STATUS.PENDING, this.state.results),
        });

        Promise.all(
            this.state.results[pageIndex]
                // seems stupid to filter this twice...
                .filter(result => result.selected && result.status === UNPUBLISHED)
                .map(result => {
                    return fetchStoryByUuid(result.uuid)
                        .then(fullStory => {
                            return fetchJsonV1('/platform/facade/story/publish/' + result.uuid,
                                {
                                    'method': 'PUT',
                                    'body': JSON.stringify({data: {
                                        ...fullStory.data[0],
                                        publishAuthorizationDateTime: Date.now(),
                                    }}),
                                })
                                .then(() => {
                                    this.setState(assign(['results', pageIndex, indexMap[result.uuid]], {status: PUBLISHED, fetchStatus: FETCH_STATUS.SUCCESS}));
                                })
                                .catch(() => messageHandler('error', `unable to publish ${result.uuid}`));
                        });
                })
        )
            .finally(() => this.setState({fetchStatus: FETCH_STATUS.SUCCESS}));
    }

    applyTagsToSelected = tags => {
        const pageIndex = this.state.pageIndex;
        const indexMap = getIndexMap(this.state.results[pageIndex]);

        this.setState({
            fetchStatus: FETCH_STATUS.PENDING,
            results: set([this.state.pageIndex, elems, when(r => r.selected), 'fetchStatus'], FETCH_STATUS.PENDING, this.state.results),
        });

        Promise.all(
            this.state.results[pageIndex]
                .filter(result => result.selected)
                .map(result => {
                    return fetchStoryByUuid(result.uuid)
                        .then(response => {
                            const fullStory = response.data[0];
                            fullStory.tags = [...fullStory.tags, ...tags];

                            return saveStory(fullStory)
                                .then(() => {
                                    this.setState(set(['results', pageIndex, indexMap[result.uuid], 'fetchStatus'], FETCH_STATUS.SUCCESS));
                                })
                                .catch(() => messageHandler('error', `unable to publish ${result.uuid}`));
                        });
                })
        )
            .finally(() => this.setState({fetchStatus: FETCH_STATUS.SUCCESS}));
    }

    prependTagToList = (newTag) => {
        const {tagList} = this.state;
        const inList = tagList.some(t => {
            return t.name === newTag.name && t.dimension === newTag.dimension;
        });

        if (!inList){
            this.setState({tagList: [newTag, ...this.state.tagList]});
        }
    }

    toggleTagPanel = () => this.setState({showTagPanel: !this.state.showTagPanel});

    setPageIndex = i => {
        if (i !== this.state.pageIndex && this.state.fetchStatus !== FETCH_STATUS.PENDING){
            this.setState({
                allSelected: false,
                pageIndex: i,
                // deselect all items to simplify "select all" behavior
                results: set([elems, elems, 'selected'], false, this.state.results),
            });
        }
    }

    renderStatusIndicator(status){
        let message;

        switch (status) {
            case FETCH_STATUS.FAIL:
                message = 'There was a problem with your request';
                break;
            case FETCH_STATUS.PENDING:
                return <TopLoader />;
            case FETCH_STATUS.EMPTY:
                message = 'There were no results.';
                break;
            case FETCH_STATUS.SUCCESS:
            default:
                return null;
        }

        return <h3 className={classes.status}>{message}</h3>;
    }

    renderPagination(){
        if (this.state.results.length < 2){
            return null;
        }
        return (
            <div className={classes.pagination}>
                { this.state.results.map((page, i) => (
                    <span
                        className={i === this.state.pageIndex ? classes.activePage : undefined}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`pagination-${i}`}
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={() => this.setPageIndex(i)}
                    >
                        {i + 1}
                    </span>
                ))}
            </div>
        );
    }

    render() {
        const {
            state: {
                allSelected,
                fetchStatus,
                results,
                searchQuery,
                pageIndex,
                tagList,
            },
        } = this;

        const someChecked = results[pageIndex] && results[pageIndex].some(result => result.selected);
        const totalResults = results.reduce((sum, page) => (sum + page.length), 0);

        return (
            <section className={classes.tableView}>
                <header>
                    <Search
                        value={searchQuery}
                        onChange={this.setSearchQuery}
                    />
                    <Button
                        className={classes.search}
                        onClick={this.handleSearch}
                    >
                        Search
                    </Button>
                </header>
                <BulkEditControls
                    results={results[pageIndex]}
                    disabled={!someChecked || fetchStatus === FETCH_STATUS.PENDING}
                    publishAll={this.publishAll}
                    toggleTagPanel={this.toggleTagPanel}
                    pullAll={this.pullAll}
                />
                {results[pageIndex] && results[pageIndex].length ?
                    <h3 className={classes.count}>{totalResults} results</h3>
                    : null
                }
                {this.renderPagination()}
                {this.renderStatusIndicator(this.state.fetchStatus)}
                {results[pageIndex] && results[pageIndex].length ?
                    <BulkEditTable
                        allSelected={allSelected}
                        toggleSelectAll={this.toggleSelectAll}
                        onItemSelect={this.handleItemSelect}
                        results={results[pageIndex]}
                    />
                    : null
                }
                <TagPanel
                    visible={this.state.showTagPanel}
                    onApplyTags={this.applyTagsToSelected}
                    onAddTag={this.prependTagToList}
                    onTagSearch={this.handleTagSearch}
                    toggleVisibility={this.toggleTagPanel}
                    tagList={tagList}
                />
            </section>
        );
    }
}
